import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChefHat, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth-context";
import { useCountry, COUNTRIES } from "@/lib/country-context";
import { useToast } from "@/hooks/use-toast";

const CUISINES = [
  "Ghanaian", "Nigerian", "Nigerian Grills", "Ghanaian Street", "Nigerian Home",
  "West African", "Continental", "Other",
];

export default function RestaurantSignupPage() {
  const { signupRestaurant } = useAuth();
  const { setCountry } = useCountry();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: "", owner_name: "", country: "GH", phone_local: "",
    password: "", cuisine: "Ghanaian", address: "",
  });
  const [loading, setLoading] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setPhoneLocal = (raw) =>
    setForm((f) => ({ ...f, phone_local: (raw || "").replace(/\D/g, "") }));

  useEffect(() => {
    if (form.country === "GH" || form.country === "NG") setCountry(form.country);
  }, [form.country, setCountry]);

  const dialCode = COUNTRIES[form.country].dialCode;

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast({ title: "Password must be at least 6 characters", variant: "destructive" });
      return;
    }
    if (form.phone_local.length < 6) {
      toast({ title: "Enter a valid phone number", variant: "destructive" });
      return;
    }
    const fullPhone = `${dialCode}${form.phone_local.replace(/^0+/, "")}`;
    setLoading(true);
    try {
      await signupRestaurant({
        name: form.name,
        owner_name: form.owner_name,
        phone: fullPhone,
        country: form.country,
        password: form.password,
        cuisine: form.cuisine,
        address: form.address || null,
      });
      toast({ title: "Restaurant registered!", description: "Welcome to Cravve." });
      navigate("/restaurant-portal", { replace: true });
    } catch (err) {
      toast({
        title: "Registration failed",
        description: err?.response?.data?.detail || "Try again",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6 py-10">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <div className="inline-flex w-12 h-12 rounded-2xl bg-primary text-primary-foreground items-center justify-center mb-6">
            <ChefHat className="w-6 h-6" />
          </div>
          <h1 className="font-serif text-3xl font-bold">Register your restaurant</h1>
          <p className="text-muted-foreground mt-2">Get on Cravve and reach hungry customers in Ghana and Nigeria.</p>
        </div>

        <form onSubmit={submit} className="bg-card border rounded-2xl p-6 space-y-4 shadow-sm">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="r-name">Restaurant name</Label>
              <Input id="r-name" required value={form.name} onChange={upd("name")}
                placeholder="Jollof Junction" data-testid="r-signup-name" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="r-owner">Owner name</Label>
              <Input id="r-owner" required value={form.owner_name} onChange={upd("owner_name")}
                placeholder="Your name" data-testid="r-signup-owner" />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Country</Label>
            <div className="grid grid-cols-2 gap-2">
              {["GH", "NG"].map((code) => {
                const c = COUNTRIES[code];
                const active = form.country === code;
                return (
                  <button
                    key={code}
                    type="button"
                    onClick={() => setForm({ ...form, country: code })}
                    data-testid={`r-signup-country-${code}`}
                    className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left ${
                      active ? "border-primary bg-primary/5" : "border-input hover:border-muted-foreground/40"
                    }`}
                  >
                    <span className="text-2xl">{c.flag}</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm leading-tight">{c.name}</p>
                      <p className="text-xs text-muted-foreground">{c.currencySymbol} {c.currency}</p>
                    </div>
                    {active && <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="r-phone">Phone number</Label>
            <div className="flex items-stretch h-10 rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 overflow-hidden">
              <div className="flex items-center gap-1 px-3 bg-muted text-muted-foreground text-sm font-semibold select-none border-r">
                <span className="text-base leading-none">{COUNTRIES[form.country].flag}</span>
                <span>{dialCode}</span>
              </div>
              <input
                id="r-phone"
                type="tel"
                inputMode="numeric"
                required
                value={form.phone_local}
                onChange={(e) => setPhoneLocal(e.target.value)}
                placeholder={form.country === "GH" ? "24 123 4567" : "801 234 5678"}
                className="flex-1 bg-transparent px-3 text-sm placeholder:text-muted-foreground focus:outline-none"
                data-testid="r-signup-phone"
                autoComplete="tel-national"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Country code <span className="font-semibold text-foreground">{dialCode}</span> is locked to your country selection.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="r-cuisine">Cuisine</Label>
              <select id="r-cuisine" value={form.cuisine} onChange={upd("cuisine")}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                data-testid="r-signup-cuisine">
                {CUISINES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="r-address">Address</Label>
              <Input id="r-address" value={form.address} onChange={upd("address")}
                placeholder="12 Oxford St, Osu, Accra" data-testid="r-signup-address" />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="r-pw">Password</Label>
            <Input id="r-pw" type="password" required minLength={6} value={form.password}
              onChange={upd("password")} placeholder="At least 6 characters" data-testid="r-signup-password" />
          </div>
          <Button type="submit" className="w-full h-11 font-semibold" disabled={loading} data-testid="r-signup-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Register restaurant"}
          </Button>
        </form>

        <p className="text-center mt-6 text-sm text-muted-foreground">
          Already registered?{" "}
          <Link to="/restaurant-portal/signin" className="text-primary font-semibold hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
